import { CalendarMonthOutlined, LocalGasStationOutlined, SpeedOutlined } from "@mui/icons-material";
import { Box, styled, TextField, Typography } from "@mui/material";
import { FUEL_LEVELS, FUEL_MAX, FUEL_MIN } from "@repo/contracts/fuel";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import DatePickerControl from "@/components/common/inputs/DatePicker";

const Card = styled(Box)(({ theme }) => ({
  width: "100%",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  padding: theme.spacing(2, 2.5),
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.25),
  padding: theme.spacing(1, 0),
  "&:not(:last-of-type)": {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const RowIcon = styled(Box)(({ theme }) => ({
  display: "flex",
  color: theme.palette.text.disabled,
  "& svg": {
    fontSize: 18,
  },
}));

const RowLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.8125rem",
  minWidth: "70px",
  textTransform: "capitalize",
}));

const RowValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: "0.875rem",
  marginLeft: "auto",
  fontVariantNumeric: "tabular-nums",
}));

const PlaceholderValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontSize: "0.875rem",
  marginLeft: "auto",
  fontStyle: "italic",
}));

const GaugeSegment = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "editing" && prop !== "level",
})<{ active: boolean; editing: boolean; level: number }>(({ theme, active, editing, level }) => ({
  width: "13px",
  height: "16px",
  borderRadius: "2px",
  cursor: editing ? "pointer" : "default",
  backgroundColor: active
    ? level <= 3
      ? theme.palette.warning.main
      : theme.palette.success.main
    : theme.palette.action.disabledBackground,
  transition: "background-color 0.15s ease, transform 0.15s ease",
  "&:hover": editing
    ? {
        transform: "scaleY(1.15)",
      }
    : undefined,
}));

const GaugeContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginLeft: "auto",
});

type Props = {
  date: string | Date;
  km: number;
  fuel: number;
  title: string;
  isEditing?: boolean;
  mileageValue?: string;
  onChangeMileage?: (val: string) => void;
  fuelValue?: string;
  onChangeFuel?: (val: string) => void;
  dateValue?: Date;
  onChangeDate?: (val: Date | undefined) => void;
};

function VehicleDetails({
  date,
  km,
  fuel,
  title,
  isEditing = false,
  mileageValue = "",
  onChangeMileage,
  fuelValue = "",
  onChangeFuel,
  dateValue,
  onChangeDate,
}: Props) {
  const { t } = useTranslation();

  const formatDate = (rawDate: string | Date | undefined) => {
    if (!rawDate) return null;
    try {
      const parsed = typeof rawDate === "string" ? parseISO(rawDate) : rawDate;
      return format(parsed, "dd/MM/yyyy HH:mm");
    } catch (_e) {
      return null;
    }
  };

  const formattedDate = formatDate(date);
  const hasMileage = km !== -1 && km !== undefined && km !== null;
  const clampFuel = (value: number) => Math.min(FUEL_MAX, Math.max(FUEL_MIN, value));

  const displayFuelLevel = isEditing
    ? fuelValue === "" || Number.isNaN(Number(fuelValue))
      ? -1
      : clampFuel(Number(fuelValue))
    : fuel !== undefined && fuel !== null
      ? clampFuel(fuel)
      : -1;
  const showGauge = isEditing || displayFuelLevel >= 0;

  const handleSegmentClick = (index: number) => {
    const newValue = index + 1;
    if (newValue === 1 && Number(fuelValue) === 1) {
      onChangeFuel?.("0");
    } else {
      onChangeFuel?.(String(newValue));
    }
  };

  return (
    <Card sx={{ border: "none" }}>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 500, mb: 1.5, textTransform: "capitalize" }}
      >
        {title}
      </Typography>

      <InfoRow>
        <RowIcon>
          <CalendarMonthOutlined />
        </RowIcon>
        <RowLabel>{t("common.shared.date")}</RowLabel>
        {isEditing ? (
          <Box
            sx={{
              marginLeft: "auto",
              maxWidth: "200px",
              "& .MuiInputBase-root": {
                height: "32px",
                fontSize: "0.8125rem",
              },
              "& .MuiInputBase-input": {
                padding: "4px 0",
              },
            }}
          >
            <DatePickerControl
              type="dateTime"
              value={dateValue}
              onChange={onChangeDate || (() => {})}
            />
          </Box>
        ) : formattedDate ? (
          <RowValue>{formattedDate}</RowValue>
        ) : (
          <PlaceholderValue>{t("common.shared.notAvailable", "Not available")}</PlaceholderValue>
        )}
      </InfoRow>

      <InfoRow>
        <RowIcon>
          <SpeedOutlined />
        </RowIcon>
        <RowLabel>{t("common.shared.mileage")}</RowLabel>
        {isEditing ? (
          <TextField
            size="medium"
            type="number"
            value={mileageValue}
            onChange={(e) => onChangeMileage?.(e.target.value)}
            sx={{
              maxWidth: "140px",
              // fieldset: {
              //   legend: { display: "none" },
              // },
              marginLeft: "auto",
              "& .MuiInputBase-input": {
                padding: "10px",
                textAlign: "right",
              },
            }}
          />
        ) : hasMileage ? (
          <RowValue>{km.toLocaleString()} km</RowValue>
        ) : (
          <PlaceholderValue>{t("common.shared.notAvailable", "Not available")}</PlaceholderValue>
        )}
      </InfoRow>

      <InfoRow>
        <RowIcon>
          <LocalGasStationOutlined />
        </RowIcon>
        <RowLabel>{t("common.shared.fuel")}</RowLabel>
        {showGauge ? (
          <GaugeContainer>
            <Box sx={{ display: "flex", gap: "3px" }}>
              {Array.from({ length: FUEL_LEVELS }).map((_, i) => (
                <GaugeSegment
                  key={i}
                  active={displayFuelLevel > i}
                  editing={isEditing}
                  level={displayFuelLevel}
                  onClick={isEditing ? () => handleSegmentClick(i) : undefined}
                />
              ))}
            </Box>
            {displayFuelLevel >= 0 && (
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  color: "text.secondary",
                  fontVariantNumeric: "tabular-nums",
                  minWidth: "28px",
                }}
              >
                {displayFuelLevel}/{FUEL_LEVELS}
              </Typography>
            )}
          </GaugeContainer>
        ) : (
          <PlaceholderValue>{t("common.shared.notAvailable", "Not available")}</PlaceholderValue>
        )}
      </InfoRow>
    </Card>
  );
}

export default VehicleDetails;
